/**
 * AuthContext — account verplicht voor de tool (geen anonieme trial-bootstrap)
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import { api, getStoredAuthToken, setStoredAuthToken } from './api';

/** Na deze tijd zonder muis/toetsenbord/touch wordt de sessie client-side beëindigd (server-sessie kan langer geldig zijn). */
const IDLE_LOGOUT_MS = 45 * 60 * 1000;
import type { TenantBillingPublic } from '../types/billing';

export interface AuthUser {
  tenant_id: string;
  user_id?: string | null;
  email?: string | null;
  role?: string | null;
  authenticated?: boolean;
  session_kind?: string | null;
  /** Weergavenaam (optioneel); anders e-mail-lokaal deel in UI */
  display_name?: string | null;
  has_avatar?: boolean;
  /** Publieke naam van de organisatie (tenants.name) */
  tenant_display_name?: string | null;
  tenant_billing?: TenantBillingPublic;
}

interface AuthContextValue {
  user: AuthUser | null;
  isLoading: boolean;
  /** Tenant-eerste accountbeheerder (`admin`) of platformbeheerder: organisatie, abonnement, facturatie in het account. */
  isAccountManager: boolean;
  /** Zelfde als `isSuperAdmin` — alleen `superadmin` mag /admin (Platformbeheer). */
  isPlatformAdmin: boolean;
  /** Alleen rol `superadmin`: volledig Platformbeheer onder /admin. */
  isSuperAdmin: boolean;
  refreshUser: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue>({
  user: null,
  isLoading: true,
  isAccountManager: false,
  isPlatformAdmin: false,
  isSuperAdmin: false,
  refreshUser: async () => {},
  logout: async () => {},
});

async function loadUser(): Promise<AuthUser> {
  if (!getStoredAuthToken()) {
    return {
      tenant_id: 'default',
      user_id: null,
      email: null,
      role: null,
      authenticated: false,
      session_kind: null,
    };
  }

  const data = (await api.getAuthMe()) as AuthUser;

  if (!data.authenticated && getStoredAuthToken()) {
    setStoredAuthToken(null);
    return {
      tenant_id: 'default',
      user_id: null,
      email: null,
      role: null,
      authenticated: false,
      session_kind: null,
    };
  }

  return data;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const refreshUser = useCallback(async () => {
    try {
      const next = await loadUser();
      setUser(next);
    } catch {
      setUser(null);
    }
  }, []);

  const logout = useCallback(async () => {
    await api.authLogout();
    setUser({
      tenant_id: 'default',
      user_id: null,
      email: null,
      role: null,
      authenticated: false,
      session_kind: null,
    });
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const next = await loadUser();
        if (!cancelled) setUser(next);
      } catch {
        if (!cancelled) setUser(null);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!user?.authenticated) return;
    let lastActivity = Date.now();
    const bump = () => {
      lastActivity = Date.now();
    };
    const opts: AddEventListenerOptions = { passive: true };
    for (const ev of ['mousedown', 'keydown', 'touchstart', 'scroll'] as const) {
      window.addEventListener(ev, bump, opts);
    }
    const tick = window.setInterval(() => {
      if (Date.now() - lastActivity < IDLE_LOGOUT_MS) return;
      window.clearInterval(tick);
      for (const ev of ['mousedown', 'keydown', 'touchstart', 'scroll'] as const) {
        window.removeEventListener(ev, bump);
      }
      void (async () => {
        await api.authLogout();
        setStoredAuthToken(null);
        setUser({
          tenant_id: 'default',
          user_id: null,
          email: null,
          role: null,
          authenticated: false,
          session_kind: null,
        });
        const path = window.location.pathname;
        if (!path.startsWith('/login') && !path.startsWith('/register') && !path.startsWith('/forgot-password')) {
          window.location.href = '/login?reden=inactiviteit';
        }
      })();
    }, 60_000);
    return () => {
      window.clearInterval(tick);
      for (const ev of ['mousedown', 'keydown', 'touchstart', 'scroll'] as const) {
        window.removeEventListener(ev, bump);
      }
    };
  }, [user?.authenticated]);

  const role = (user?.role ?? '').toLowerCase();
  const isAccountManager = role === 'admin' || role === 'superadmin';
  const isSuperAdmin = role === 'superadmin';
  const isPlatformAdmin = isSuperAdmin;

  return (
    <AuthContext.Provider
      value={{ user, isLoading, isAccountManager, isPlatformAdmin, isSuperAdmin, refreshUser, logout }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
